/* eslint-disable no-unused-vars */

import React, { useEffect, useState } from 'react'
import styles from './styles.module.scss'
import RegularTable from './RegularTable'
import VerticalTable from './VerticalTable'
import { useDispatch, useSelector } from 'react-redux'
import { togglePopup } from '../../../Redux/popups/popupSlice'
import { ReactComponent as LayoutHIcon } from '../../../assets/icons/h_layout.svg'
import { ReactComponent as LayoutVIcon } from '../../../assets/icons/v_layout.svg'
import { useTranslation } from 'react-i18next'
import QuickEditModal from '../PatientPage/components/PatientAppointments/QuickEditModal'
import { getAllUsers } from '../../../Redux/users/usersSlice'
import { updateAppointment, deleteAppointment } from '../../../Redux/appointments/appointmentSlice'
import { arrayOfTimesGeneral } from './timeLines'

// Albanian date formatter
const formatDateInAlbanian = (date) => {
  const daysInAlbanian = {
    0: 'E Dielë',
    1: 'E Hënë', 
    2: 'E Martë',
    3: 'E Mërkurë',
    4: 'E Enjte',
    5: 'E Premte',
    6: 'E Shtunë'
  }
  
  const monthsInAlbanian = {
    0: 'Janar',
    1: 'Shkurt', 
    2: 'Mars',
    3: 'Prill',
    4: 'Maj',
    5: 'Qershor',
    6: 'Korrik',
    7: 'Gusht',
    8: 'Shtator',
    9: 'Tetor',
    10: 'Nëntor',
    11: 'Dhjetor'
  }
  
  const day = daysInAlbanian[date.getDay()]
  const dayNumber = date.getDate()
  const month = monthsInAlbanian[date.getMonth()]
  const year = date.getFullYear()
  
  return `${day} ${dayNumber} ${month} ${year}`
}

const Appointments = () => {
  const { appointments } = useSelector((state) => state.appointments)
  const { users } = useSelector((state) => state.users)
  const { operations } = useSelector((state) => state.operations)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [viewState, setViewState] = useState(window.innerWidth > 1500 ? 1 : 2)
  const [selectedDate, setSelectedDate] = useState(new Date())

  // Quick Edit Modal State
  const [quickEditApt, setQuickEditApt] = useState(null)
  const [quickEditForm, setQuickEditForm] = useState({
    feedback: '',
    examination: '',
    therapy: '',
    description: '',
    selectedTeeth: [],
    selectedType: 'all',
    doctor_id: '',
    service_name: '',
    category: 'general',
  })

  // Fetch users if not available
  useEffect(() => {
    if (!users?.length) {
      dispatch(getAllUsers())
    }
  }, [users, dispatch])

  const handleResize = () => {
    if (window.innerWidth > 1500) {
      if (viewState !== 1) {
        setViewState(1)
      }
    } else {
      if (viewState !== 2) {
        setViewState(2)
      }
    }
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [viewState])

  const createAppointment = ({ time, date }) => {
    dispatch(
      togglePopup({
        pointer: 3,
        data: { 
          time, 
          date, 
          creating: true,
          hideRoomAndTeeth: true
        }
      })
    )
  }

  const openQuickEdit = (appointment) => {
    // Convert the appointment time to the correct format
    const appointmentTime = appointment.fromStr || '09:00';
    const formattedTime = arrayOfTimesGeneral.find(time => time.displayValue === appointmentTime)?.displayValue || '09:00';
    
    setQuickEditApt(appointment)
    setQuickEditForm({
      feedback: appointment.feedback || '',
      examination: appointment.examination || '',
      therapy: appointment.therapy || '',
      description: appointment.description || '',
      selectedTeeth: Array.isArray(appointment.teeth_list) ? appointment.teeth_list : [],
      selectedType: 'all',
      doctor_id: appointment.doctor_id || '',
      service_name: appointment.name || appointment.operation_name || '',
      category: appointment.category || 'general',
      date: appointment.date || new Date().toISOString().split('T')[0],
      time: formattedTime
    })
  }

  const handleQuickEditChange = (e) => {
    const { name, value } = e.target
    setQuickEditForm({
      ...quickEditForm,
      [name]: value
    })
  }

  const handleToothClick = (toothNumber) => {
    let newTeethArray = [...quickEditForm.selectedTeeth];
    if (newTeethArray.includes(toothNumber)) {
      newTeethArray = newTeethArray.filter((tooth) => tooth !== toothNumber);
    } else {
      newTeethArray.push(toothNumber);
    }
    setQuickEditForm({
      ...quickEditForm,
      selectedTeeth: newTeethArray
    });
  }

  const saveQuickEdit = () => {
    if (!quickEditApt) return;
    
    // Find the selected doctor to get the name
    const selectedDoctor = users.find(user => user.id === quickEditForm.doctor_id);
    
    // Find the selected operation
    const selectedOperation = operations?.find(op => op.op_name === quickEditForm.service_name);
    
    // Calculate time values correctly
    const [hours, minutes] = quickEditForm.time.split(':').map(Number);
    const timeValue = hours + (minutes / 60);
    const nextTimeSlot = arrayOfTimesGeneral.find(time => time.value === timeValue + 0.5)?.displayValue || '09:30';
    
    const updatedAppointment = {
      ...quickEditApt,
      feedback: quickEditForm.feedback,
      examination: quickEditForm.examination,
      therapy: quickEditForm.therapy,
      description: quickEditForm.description,
      name: quickEditForm.service_name,
      doctor_id: quickEditForm.doctor_id || quickEditApt.doctor_id,
      doctor_name: selectedDoctor ? selectedDoctor.displayName : quickEditApt.doctor_name,
      // Update date and time fields
      date: quickEditForm.date,
      from: timeValue,
      to: timeValue + 0.5,
      fromStr: quickEditForm.time,
      toStr: nextTimeSlot,
      // Update operation details
      operation_id: selectedOperation?.id || quickEditApt.operation_id,
      operation_name: quickEditForm.service_name,
      // Shto fushën services me emrin e ri
      services: [quickEditForm.service_name],
      // Preserve other important fields
      category: quickEditApt.category,
      unit_multiplier: quickEditApt.unit_multiplier,
      array_of_payments: quickEditApt.array_of_payments,
      price: selectedOperation?.price || quickEditApt.price,
      is_paid: quickEditApt.is_paid,
      is_ongoing: quickEditApt.is_ongoing,
      color: quickEditApt.color
    };
    
    if (quickEditForm.category === 'stomatology') {
      updatedAppointment.teeth_list = quickEditForm.selectedTeeth;
    }
    
    dispatch(updateAppointment(updatedAppointment));
    setQuickEditApt(null);
  };

  const cancelQuickEdit = () => {
    setQuickEditApt(null)
  }

  const autoResizeTextarea = (e) => {
    e.target.style.height = 'auto';
    e.target.style.height = (e.target.scrollHeight) + 'px';
  };

  const handleEditApt = (apt) => {
    openQuickEdit(apt)
  }

  const handleDateChange = (e) => {
    const date = new Date(e.target.value)
    // validate date
    if (date.toString() === 'Invalid Date') {
      return
    }
    setSelectedDate(date)
  }

  const handleDeleteApt = (appointment) => {
    if (!appointment) return;
    dispatch(deleteAppointment({ id: appointment.id, keepInPatientRecord: true }));
    setQuickEditApt(null);
  };

  const filteredApts = appointments?.filter((apt) => {
    // Skip appointments that are marked as deleted from calendar
    if (apt.isDeletedFromCalendar) {
      return false;
    }
    // Exclude pulmonology appointments from the calendar
    if (apt.category === 'pulmonology') {
      return false;
    }
    return true
  })

  return (
    <div className={styles.container}>
      <div className={styles.header_container}>
        <div className={styles.left_view_control}>
          <span className={styles.date_today_view}>
            {formatDateInAlbanian(new Date(selectedDate))}
          </span>
          <input
            onChange={handleDateChange}
            type='date'
            defaultValue={new Date().toISOString().split('T')[0]}
            className={styles.date_input}
          />
        </div>
        <div className={styles.right_view_control}>
          <div className={styles.view_switcher}>
            <button className={`${styles.view_switcher_btn} ${viewState === 1 ? styles.active : ''}`} onClick={() => setViewState(1)}>
              <LayoutHIcon className={`${styles.vh_icon}`} />
            </button>
            <button className={`${styles.view_switcher_btn} ${viewState === 2 ? styles.active : ''}`} onClick={() => setViewState(2)}>
              <LayoutVIcon className={`${styles.vh_icon}`} />
            </button>
          </div>
        </div>
      </div>
      {viewState === 1 ? (
        <RegularTable
          selectedDate={selectedDate}
          create={createAppointment}
          appointmentsArray={filteredApts || []}
          handleEditApt={handleEditApt}
          t={t}
          users={users}
        />
      ) : (
        <VerticalTable
          selectedDate={selectedDate}
          create={createAppointment}
          appointmentsArray={filteredApts || []}
          handleEditApt={handleEditApt}
          t={t}
        />
      )}
      {quickEditApt && (
        <QuickEditModal
          quickEditApt={quickEditApt}
          quickEditForm={quickEditForm}
          users={users}
          handleQuickEditChange={handleQuickEditChange}
          handleToothClick={handleToothClick}
          setQuickEditForm={setQuickEditForm}
          autoResizeTextarea={autoResizeTextarea}
          onSaveQuickEdit={saveQuickEdit}
          cancelQuickEdit={cancelQuickEdit}
          handleDeleteApt={handleDeleteApt}
          isFromCalendar={true}
          t={t}
          setQuickEditApt={setQuickEditApt}
        />
      )}
    </div>
  )
}

export default Appointments
